import Skeleton from 'react-loading-skeleton'
import { Tooltip } from 'react-tooltip'
import { usePartnerContext } from '../../../../../context'
import { Image, Path, Svg, View } from '../../compiler'
import { colors, typoStyles } from '../../constants'
import { useTranslationContext } from '../../context/general-translations'
import { hexToRGB, isNumber } from '../../utils'
import MarkdownText from '../MarkdownText'
import AverageIcon from './AverageIcon'
import InitialsIcon from './InitialsIcon'
import UserDimensionValue from './UserDimensionValue'
import UserDimensionValueWithIcon from './UserDimensionValueWithIcon'

const COMPANY_ICON =
  'https://aivy-assets.s3.eu-central-1.amazonaws.com/pdf_reports/images/company-icon.png'
const COMPANY_ICON_SIZE = 26
const COMPANY_ICON_HALF = COMPANY_ICON_SIZE / 2

const getColorsAndPosition = (scores) => {
  const {
    HEROS_JOURNEY_score_coopcomp: coopcomp,
    HEROS_JOURNEY_score_orderchaos: orderchaos
  } = scores

  const transform = `translate(${-COMPANY_ICON_HALF}px, ${-COMPANY_ICON_HALF}px)`

  if (coopcomp === 0 && orderchaos === 0) {
    // bottom-left green, others orange/gray
    return {
      companyIconPosition: { left: '25%', top: '75%', transform },
      quadrantColors: {
        topLeft: hexToRGB(colors.secondaryYellow, 0.5),
        topRight: colors.paleWhiteBlue,
        bottomLeft: hexToRGB(colors.brightGreen, 0.5),
        bottomRight: hexToRGB(colors.secondaryYellow, 0.5)
      }
    }
  } else if (coopcomp === 0 && orderchaos === 100) {
    // top-left green, others orange/gray
    return {
      companyIconPosition: { left: '25%', top: '25%', transform },
      quadrantColors: {
        topLeft: hexToRGB(colors.brightGreen, 0.5),
        topRight: hexToRGB(colors.secondaryYellow, 0.5),
        bottomLeft: hexToRGB(colors.secondaryYellow, 0.5),
        bottomRight: colors.paleWhiteBlue
      }
    }
  } else if (coopcomp === 100 && orderchaos === 0) {
    // bottom-right green, others orange/gray
    return {
      companyIconPosition: { left: '75%', top: '75%', transform },
      quadrantColors: {
        topLeft: colors.paleWhiteBlue,
        topRight: hexToRGB(colors.secondaryYellow, 0.5),
        bottomLeft: hexToRGB(colors.secondaryYellow, 0.5),
        bottomRight: hexToRGB(colors.brightGreen, 0.5)
      }
    }
  } else if (coopcomp === 100 && orderchaos === 100) {
    // top-right green, others orange/gray
    return {
      companyIconPosition: { left: '75%', top: '25%', transform },
      quadrantColors: {
        topLeft: hexToRGB(colors.secondaryYellow, 0.5),
        topRight: hexToRGB(colors.brightGreen, 0.5),
        bottomLeft: colors.paleWhiteBlue,
        bottomRight: hexToRGB(colors.secondaryYellow, 0.5)
      }
    }
  } else {
    // team-analyse
    return {
      companyIconPosition: { display: 'none' },
      quadrantColors: {
        topLeft: colors.paleWhiteBlue,
        topRight: colors.paleWhiteBlue,
        bottomLeft: colors.paleWhiteBlue,
        bottomRight: colors.paleWhiteBlue
      }
    }
  }
}

const quadrantStyle = {
  height: 140,
  width: 140,
  borderRadius: 4,
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center'
}

// x-axis -> coopcomp.
// y-axis -> orderchaos
const CoordinateSystem = ({
  companyCultureScores = {},
  userCultureScores = null,
  teamInputValues = [],
  withCompanyIcon = false,
  isLoading = false
}) => {
  const { tGeneral } = useTranslationContext()
  const { partner } = usePartnerContext()

  const { companyIconPosition, quadrantColors } =
    getColorsAndPosition(companyCultureScores)

  const orderchaos_average =
    teamInputValues.reduce((sum, obj) => sum + obj.culturalFit.orderchaos, 0) /
    teamInputValues.length

  const coopcomp_average =
    teamInputValues.reduce((sum, obj) => sum + obj.culturalFit.coopcomp, 0) /
    teamInputValues.length

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <Skeleton width={510} height={356} />
      </div>
    )
  }

  return (
    <View
      style={{
        paddingVertical: 30 + 33,
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <View
        style={{
          position: 'absolute',
          height: 356,
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: 100,
          alignItems: 'center'
        }}
      >
        <MarkdownText style={typoStyles.SmallBlack}>
          {tGeneral('orderchaos_100_label')}
        </MarkdownText>
        <MarkdownText style={typoStyles.SmallBlack}>
          {tGeneral('orderchaos_0_label')}
        </MarkdownText>
      </View>
      <View
        style={{
          position: 'absolute',
          width: 510,
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 100,
          flexDirection: 'row'
        }}
      >
        <MarkdownText style={typoStyles.SmallBlack}>
          {tGeneral('coopcomp_0_label')}
        </MarkdownText>
        <MarkdownText style={typoStyles.SmallBlack}>
          {tGeneral('coopcomp_100_label')}
        </MarkdownText>
      </View>
      <View style={{ position: 'absolute' }}>
        <Svg
          width='8'
          height='314'
          viewBox='0 0 8 314'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <Path
            d='M4.35355 0.646447C4.15829 0.451184 3.84171 0.451184 3.64645 0.646447L0.464466 3.82843C0.269204 4.02369 0.269204 4.34027 0.464466 4.53553C0.659728 4.7308 0.976311 4.7308 1.17157 4.53553L4 1.70711L6.82843 4.53553C7.02369 4.7308 7.34027 4.7308 7.53553 4.53553C7.7308 4.34027 7.7308 4.02369 7.53553 3.82843L4.35355 0.646447ZM3.64645 313.354C3.84171 313.549 4.15829 313.549 4.35355 313.354L7.53553 310.172C7.7308 309.976 7.7308 309.66 7.53553 309.464C7.34027 309.269 7.02369 309.269 6.82843 309.464L4 312.293L1.17157 309.464C0.976311 309.269 0.659728 309.269 0.464466 309.464C0.269204 309.66 0.269204 309.976 0.464466 310.172L3.64645 313.354ZM3.5 1L3.5 313H4.5L4.5 1H3.5Z'
            fill='#45586B'
          />
        </Svg>
      </View>
      <View style={{ position: 'absolute' }}>
        <Svg
          width='314'
          height='8'
          viewBox='0 0 314 8'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <Path
            d='M313.354 4.35355C313.549 4.15829 313.549 3.84171 313.354 3.64645L310.172 0.464466C309.976 0.269204 309.66 0.269204 309.464 0.464466C309.269 0.659728 309.269 0.976311 309.464 1.17157L312.293 4L309.464 6.82843C309.269 7.02369 309.269 7.34027 309.464 7.53553C309.66 7.7308 309.976 7.7308 310.172 7.53553L313.354 4.35355ZM0.646423 3.64645C0.451172 3.84171 0.451172 4.15829 0.646423 4.35355L3.82843 7.53553C4.02368 7.7308 4.34027 7.7308 4.53552 7.53553C4.73077 7.34027 4.73077 7.02369 4.53552 6.82843L1.70709 4L4.53552 1.17157C4.73077 0.976311 4.73077 0.659728 4.53552 0.464466C4.34027 0.269204 4.02368 0.269204 3.82843 0.464466L0.646423 3.64645ZM313 3.5L1 3.5V4.5L313 4.5V3.5Z'
            fill='#45586B'
          />
        </Svg>
      </View>
      <View
        style={{
          position: 'relative',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 10,
          width: 290,
          height: 290
        }}
      >
        <View
          style={{
            ...quadrantStyle,
            ...{ backgroundColor: quadrantColors.topLeft }
          }}
        />
        <View
          style={{
            ...quadrantStyle,
            ...{ backgroundColor: quadrantColors.topRight }
          }}
        />
        <View
          style={{
            ...quadrantStyle,
            ...{ backgroundColor: quadrantColors.bottomLeft }
          }}
        />
        <View
          style={{
            ...quadrantStyle,
            ...{ backgroundColor: quadrantColors.bottomRight }
          }}
        />
        {withCompanyIcon && (
          <>
            <Image
              data-tooltip-id='company-icon'
              data-tooltip-content={partner.name}
              style={{
                position: 'absolute',
                width: COMPANY_ICON_SIZE,
                height: COMPANY_ICON_SIZE,
                ...companyIconPosition
              }}
              src={{ uri: COMPANY_ICON }}
            />
            <Tooltip
              id='company-icon'
              content={partner.name}
              place='top'
              variant='info'
              className='font-medium'
              style={{ backgroundColor: '#182033' }}
            />
          </>
        )}
        {!!teamInputValues.length && (
          <AverageIcon
            bottom={`${orderchaos_average}%`}
            left={`${coopcomp_average}%`}
          />
        )}
        {teamInputValues.map((member, index) => (
          <InitialsIcon
            key={index}
            left={`${member.culturalFit.coopcomp}%`}
            bottom={`${member.culturalFit.orderchaos}%`}
            label={member.label}
          />
        ))}
        {userCultureScores && isNumber(userCultureScores.group_match) && (
          <UserDimensionValueWithIcon
            left={`${userCultureScores.HEROS_JOURNEY_score_coopcomp}%`}
            bottom={`${userCultureScores.HEROS_JOURNEY_score_orderchaos}%`}
            score={userCultureScores.group_match}
          />
        )}
        {userCultureScores && !isNumber(userCultureScores.group_match) && (
          <UserDimensionValue
            left={`${userCultureScores.HEROS_JOURNEY_score_coopcomp}%`}
            bottom={`${userCultureScores.HEROS_JOURNEY_score_orderchaos}%`}
          />
        )}
      </View>
    </View>
  )
}

export default CoordinateSystem
